import { CompanyTag } from "@/components/domain/CompanyTag";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Pencil,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { type Paginated, type PackingListSummaryDto } from "@hisense/shared";
import { PageBody } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  EmptyTableRow,
  ListTableFrame,
  LIST_PAGE_BODY,
} from "@/components/ui/ListTable";
import { Td, Th, Tr } from "@/components/ui/Table";
import { Stack } from "@/components/ui/Stack";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import { useDeletePackingList, usePackingLists } from "@/api/hooks";
import { ExportButton } from "@/components/domain/ExportButton";
import { apiFetch, getLocalizedError } from "@/lib/api";
import { Select } from "@/components/ui/Select";
import { formatDate } from "@/domain/datetime";
import { useClampListPage, useListQueryState, useParamInput } from "@/lib/listQuery";
import { RowPrintButton } from "@/components/domain/RowPrintButton";
import { printPackingList } from "@/lib/printPackingList";

/** Packing-list weights are plain kilograms (drizzle real), never money-scaled. */
const fmtKg = (n: number) =>
  n.toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

export function PackingListsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const list = useListQueryState();
  const respByInput = useParamInput(list, "respBy");
  const context = list.searchParams.size ? `?${list.searchParams}` : "";
  const full = list.searchParams.get("view") === "full";
  const from = list.searchParams.get("from") ?? "";
  const to = list.searchParams.get("to") ?? "";
  const filters = {
    from: from ? new Date(from + "T00:00:00").getTime() : undefined,
    to: to ? new Date(to + "T23:59:59.999").getTime() : undefined,
    respBy: list.searchParams.get("respBy") || undefined,
    sort: list.searchParams.get("sort") || "-date",
  };
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{
    id: string;
    plNo: string;
  } | null>(null);
  const { data, isLoading, isFetching, error, refetch } = usePackingLists({
    ...filters,
    q: list.q.trim() || undefined,
    page: list.page,
    pageSize: list.pageSize,
  });
  useClampListPage(data?.total, list.clampPage);
  const del = useDeletePackingList();

  const canCreate = user ? can("records:create") : false;
  const canUpdate = user ? can("records:update") : false;
  const canDelete = user ? can("records:delete") : false;
  // Nút In không đòi quyền sửa: ai đọc được chứng từ thì in được nó, nên cột
  // thao tác luôn hiện.
  const showActions = true;

  const rows = data?.data ?? [];

  const onDelete = (id: string, plNo: string) => {
    setPendingDelete({ id, plNo });
  };

  return (
    <PageBody className={LIST_PAGE_BODY}>
      <ConfirmDialog
        open={!!pendingDelete}
        title={pendingDelete?.plNo ?? t("common.delete")}
        description={t("packingLists.confirmDelete", {
          number: pendingDelete?.plNo ?? "",
        })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        busy={del.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return;
          setDeleteError(null);
          del.mutate(pendingDelete.id, {
            onError: (err) =>
              setDeleteError(
                getLocalizedError(err, t, "packingLists.errorSave"),
              ),
            onSettled: () => setPendingDelete(null),
          });
        }}
      />
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Input
          {...list.searchInput}
          leading={<Search />}
          placeholder={t("common.search")}
          className="w-full max-w-xs"
        />
        <div className="ml-auto">
          <ExportButton<PackingListSummaryDto>
            name="packing-lists"
            title={t("nav.packingLists")}
            rows={rows}
            total={data?.total}
            disabled={isFetching}
            errorKey="packingLists.errorLoad"
            fetchPage={(page, pageSize, signal) =>
              apiFetch<Paginated<PackingListSummaryDto>>("/packing-lists", {
                query: { ...filters, q: list.q || undefined, page, pageSize },
                signal,
              })
            }
            columns={[
              { header: t("packingLists.listNo"), value: (r) => r.plNo },
              { header: t("invoices.piNo"), value: (r) => r.piNo },
              { header: t("packingLists.invoiceNo"), value: (r) => r.invoiceNo },
              { header: t("common.customer"), value: (r) => r.customerName },
              { header: t("common.shipTo"), value: (r) => r.shipTo },
              { header: t("common.date"), value: (r) => formatDate(r.date) },
              { header: t("common.cartons"), value: (r) => r.totalCtns },
              // Cân nặng chỉ xuất khi ĐỦ: một tổng cộng từ vài dòng có cân và
              // vài dòng chưa cân là con số sai, tệ hơn ô trống.
              { header: t("packingLists.netWeight"), value: (r) => (r.weightsComplete ? r.totalNw : null) },
              { header: t("packingLists.grossWeight"), value: (r) => (r.weightsComplete ? r.totalGw : null) },
            ]}
          />
        </div>
        {canCreate && (
          <Button
            size="sm"
            onClick={() => navigate(`/packing-lists/new${context}`)}
          >
            <Plus className="size-4" /> {t("packingLists.newPackingList")}
          </Button>
        )}
      </div>

      <div className="flex shrink-0 flex-wrap gap-2">
        <Input
          aria-label={t("packingWorkspace.from")}
          type="date"
          value={from}
          onChange={(e) => list.updateParam("from", e.target.value || null)}
        />
        <Input
          aria-label={t("packingWorkspace.to")}
          type="date"
          value={to}
          onChange={(e) => list.updateParam("to", e.target.value || null)}
        />
        <Input
          className="w-40"
          aria-label={t("pi.respBy")}
          placeholder={t("pi.respBy")}
          {...respByInput}
        />
        <Select
          aria-label={t("piWorkspace.sort")}
          value={filters.sort}
          onChange={(e) => list.updateParam("sort", e.target.value)}
        >
          <option value="-date">{t("piWorkspace.newest")}</option>
          <option value="date">{t("piWorkspace.oldest")}</option>
          <option value="plNo">{t("packingLists.listNo")} ↑</option>
          <option value="-plNo">{t("packingLists.listNo")} ↓</option>
        </Select>
        <Select
          aria-label={t("piWorkspace.columns")}
          value={full ? "full" : "operations"}
          onChange={(e) => list.updateParam("view", e.target.value)}
        >
          <option value="operations">{t("packingWorkspace.operations")}</option>
          <option value="full">{t("piWorkspace.fullView")}</option>
        </Select>
      </div>
      {deleteError && (
        <p
          role="alert"
          className="mt-3 rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger"
        >
          {deleteError}
        </p>
      )}

      <ListTableFrame
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        errorMessage={t("packingLists.errorLoad")}
        onRetry={() => refetch()}
        page={data?.page ?? list.page}
        pageSize={data?.pageSize ?? list.pageSize}
        total={data?.total ?? 0}
        onPageChange={list.updatePage}
        onPageSizeChange={list.updatePageSize}
      >
        <thead>
          <tr>
            <Th>#</Th>
            <Th pin>{t("packingLists.listNo")}</Th>
            <Th>{t("packingLists.piInvoice")}</Th>
            <Th>{t("common.customer")}</Th>
            <Th>{t("common.shipTo")}</Th>
            <Th>{t("packingLists.shippingMode")}</Th>
            <Th>{t("common.date")}</Th>
            <Th align="center">{t("common.cartons")}</Th>
            <Th align="center">{t("common.items")}</Th>
            <Th align="right">{t("packingLists.netWeight")}</Th>
            <Th align="right">{t("packingLists.grossWeight")}</Th>
            <Th>{t("pi.respBy")}</Th>
            {full && <Th>{t("products.created")}</Th>}
            {showActions && (
              <Th pinRight align="right">
                {t("common.actions")}
              </Th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <EmptyTableRow colSpan={full ? 14 : 13}>
              {t("common.noMatches")}
            </EmptyTableRow>
          )}
          {rows.map((r, i) => (
            <Tr key={r.id} interactive className="group">
              <Td className="font-mono text-xs text-ink-faint">
                {(list.page - 1) * list.pageSize + i + 1}
              </Td>
              <Td pin>
                <Link
                  to={`/packing-lists/${r.id}${context}`}
                  className="flex items-center gap-2 font-mono tabular font-medium text-brand group-hover:underline"
                >
                  {r.plNo}
                </Link>
              </Td>
              <Td>
                <Stack top={r.piNo} bottom={r.invoiceNo} mono />
              </Td>
              {/* Ship To là khối nhiều dòng (tên / địa chỉ / điện thoại /
                    email) chép từ PI. Trong bảng thì hiện dòng ĐẦU cho gọn và
                    đưa nguyên khối vào tooltip — dồn tất cả thành một dòng dài
                    sẽ phá vỡ chiều rộng cột. */}
              <Td className="text-xs">
                <div
                  className="max-w-60 truncate"
                  title={r.customerName ?? undefined}
                >
                  <CompanyTag region={r.customerRegion} className="mr-1.5" />
                  {r.customerName || "—"}
                </div>
              </Td>
              <Td className="max-w-[14rem] text-ink">
                <span className="block truncate" title={r.shipTo ?? undefined}>
                  {r.shipTo?.split("\n")[0] || "—"}
                </span>
              </Td>
              <Td className="text-xs text-ink-soft">{r.shippingMode ?? "—"}</Td>
              <Td className="font-mono tabular text-xs text-ink-soft">
                {formatDate(r.date)}
              </Td>
              <Td align="center" className="font-mono tabular text-ink-soft">
                {r.totalCtns}
              </Td>
              <Td align="center" className="font-mono tabular text-ink-soft">
                {r.itemCount}
              </Td>
              <Td align="right" className="font-mono">
                {r.weightsComplete ? fmtKg(r.totalNw) : "—"}
              </Td>
              <Td align="right" className="font-mono">
                {r.weightsComplete ? fmtKg(r.totalGw) : "—"}
              </Td>
              <Td className="text-xs text-ink-soft">{r.respBy ?? "—"}</Td>
              {full && (
                <Td>
                  <Stack
                    monoTop
                    top={formatDate(r.createdAt)}
                    bottom={r.createdByName}
                  />
                </Td>
              )}
              {showActions && (
                <Td pinRight align="right">
                  <div className="flex items-center justify-end gap-1">
                    <RowPrintButton
                      path={`/packing-lists/${r.id}`}
                      print={printPackingList}
                      label={`${t("common.print")}: ${r.plNo}`}
                    />
                    {canUpdate && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() =>
                          navigate(`/packing-lists/${r.id}/edit${context}`)
                        }
                        aria-label={t("common.edit")}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        disabled={del.isPending}
                        onClick={() => onDelete(r.id, r.plNo)}
                        aria-label={t("common.delete")}
                      >
                        <Trash2 className="size-3.5 text-danger" />
                      </Button>
                    )}
                  </div>
                </Td>
              )}
            </Tr>
          ))}
        </tbody>
      </ListTableFrame>
    </PageBody>
  );
}
